import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { Panel, PanelHeader, PanelBody } from '../components/ui/Panel';
import Pill from '../components/ui/Pill';
import Button from '../components/ui/Button';
import { MiniKpiGrid } from '../components/ui/Kpi';
import { EmptyState, LoadingPanel, ErrorNotice } from '../components/ui/States';
import { dateShort, dateLong, orDash, tenure, DASH } from '../lib/format';
import { canMutate } from '../lib/roles';
import { useAuth } from '../contexts/AuthContext';
import MemberAccount from '../components/MemberAccount';
import MemberMentorship, { MentorshipDetail } from '../components/MemberMentorship';
import AttendanceLetter from '../components/AttendanceLetter';
import { MemberPrograms, MemberPayments } from '../components/MemberPrograms';
import { MemberCommittees } from '../components/CommitteeRoles';
import MemberEvents from '../components/MemberEvents';
import MemberStipend from '../components/MemberStipend';

type Source = 'website' | 'ibm' | 'moodle' | 'zoom' | 'mentorship';

const STRIPE: Record<Source, string> = {
  website: 'bg-primary',
  ibm: 'bg-accent',
  moodle: 'bg-ink-2',
  zoom: 'bg-line-strong',
  mentorship: 'bg-[#8FB8BC]',
};

const SOURCE_LABEL: Record<Source, string> = {
  website: 'Website',
  ibm: 'IBM Cloud',
  moodle: 'Moodle',
  zoom: 'Zoom',
  mentorship: 'Mentorship',
};

interface Event {
  at: string;
  source: Source;
  title: string;
  detail: string;
}

interface TimelineResponse {
  user: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    moodleUserId: number | null;
    ibmCloudId: string | null;
    isActive: boolean;
    createdAt: string;
    ibmDeletedAt: string | null;
    ibmLastLoginAt: string | null;
    archivedAt: string | null;
  };
  profile: {
    organization: string | null;
    sector: string | null;
    careerSeniority: string | null;
    discipline: string | null;
    geographicRegion: string | null;
    preferredLanguage: string | null;
    preferredPronouns: string | null;
    submittedAt: string | null;
  } | null;
  timeline: {
    type: string;
    at: string;
    id: string;
    data: Record<string, unknown>;
  }[];
  moodleSummary: {
    courseName: string;
    completionStatus: string | null;
    lastAccess: string | null;
    dataDate: string | null;
    activityPairs?: { completed?: boolean }[];
  }[];
  mentorshipSummary?: {
    seasons: {
      seasonId: number;
      seasonName: string;
      seasonIsActive: boolean;
      role: string;
      isActive: boolean;
      matched: boolean;
      pairedCount: number;
      sessionsScheduled: number;
      sessionsAttended: number;
      firstSessionAt: string | null;
      lastSessionAt: string | null;
    }[];
    sessionsAttended: number;
    sessionsScheduled: number;
    roles: string[];
  } & Omit<MentorshipDetail, 'seasons'>;
}

function titleCase(value: string) {
  return value ? value.charAt(0).toUpperCase() + value.slice(1) : value;
}

const WINDOWS = [
  { key: 'all', label: 'All time', months: null },
  { key: '12', label: 'Last 12 months', months: 12 },
  { key: '3', label: 'Last 3 months', months: 3 },
];

function KeyValue({ label, value }: { label: string; value: string }) {
  return (
    <div
      className="grid items-start gap-3 py-[9px] border-b border-line last:border-b-0"
      style={{ gridTemplateColumns: '108px 1fr' }}
    >
      <span className="font-semibold text-small text-ink-2 pt-[2px]">{label}</span>
      <span className="text-table break-words">{value}</span>
    </div>
  );
}

function LinkRow({
  name,
  meta,
  linked,
  canProvision,
  onProvision,
  provisioning,
}: {
  name: string;
  meta: string;
  linked: boolean;
  canProvision?: boolean;
  onProvision?: () => void;
  provisioning?: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-4 py-[12px] border-b border-line last:border-b-0">
      <div className="min-w-0">
        <div className="text-table font-semibold">{name}</div>
        <div className="text-small text-ink-2 mt-[2px]">{meta}</div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Pill tone={linked ? 'linked' : 'notLinked'}>{linked ? 'Linked' : 'Not linked'}</Pill>
        {!linked && canProvision ? (
          <Button size="sm" variant="outline" disabled={provisioning} onClick={onProvision}>
            {provisioning ? 'Provisioning…' : 'Provision'}
          </Button>
        ) : null}
      </div>
    </div>
  );
}

export default function Member360() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { user: operator } = useAuth();
  const mutable = canMutate(operator?.role);
  const [win, setWin] = useState('all');
  const [letterOpen, setLetterOpen] = useState(false);
  const queryClient = useQueryClient();

  const provision = useMutation(
    async () =>
      (await api.post(`/users/${userId}/provision`)).data.data as {
        status: string;
        ibmCloudId: string;
        emailSent: boolean;
        emailError?: string;
        warning?: string;
      },
    { onSuccess: () => queryClient.invalidateQueries(['member-360', userId]) },
  );

  const { data, isLoading, error } = useQuery<TimelineResponse>(
    ['member-360', userId],
    async () => {
      const res = await api.get(`/analytics/members/${userId}/timeline`, { params: { limit: 200 } });
      return res.data.data;
    },
    { enabled: Boolean(userId) },
  );

  const cutoff = useMemo(() => {
    const months = WINDOWS.find((w) => w.key === win)?.months;
    if (!months) return null;
    const d = new Date();
    d.setMonth(d.getMonth() - months);
    return d;
  }, [win]);

  const events = useMemo<Event[]>(() => {
    if (!data) return [];
    const out: Event[] = [];

    data.timeline.forEach((t) => {
      if (t.type === 'registration') {
        const formId = t.data.formId as string | undefined;
        const entryId = t.data.entryId as string | undefined;
        out.push({
          at: t.at,
          source: 'website',
          title: `Registration received — Form ${orDash(formId)}`,
          detail: `Gravity Forms entry ${orDash(entryId)} · profile built from submission`,
        });
      }
      if (t.type === 'form_response') {
        const ev = t.data.event as { title: string } | null;
        const edition = String(t.data.edition ?? `Form ${t.data.formId}`);
        const year = t.data.year as number | null;
        const what = ev?.title ?? edition;
        const titles: Record<string, string> = {
          event_registration: t.data.willAttend === false ? `Declined ${what}` : `Registered for ${what}`,
          fellow_application: `Applied, Fellow intake ${t.data.cohort ?? year ?? ''}`.trim(),
          session_feedback: `Gave feedback on ${ev?.title ?? (t.data.session as string | null) ?? edition}`,
          fellow_institution: `Fellow stipend ToF${year ? `, ${year}` : ''}`,
          development_plan: `Development plan submitted — ${edition}`,
          trainee_survey: `Trainee survey — ${edition}`,
          pi_self_id: 'Self-identification submitted',
          committee_application: 'Applied to an advisory committee',
          registration: `Registration received — Form ${t.data.formId}`,
        };
        const family = String(t.data.family);
        out.push({
          at: t.at,
          source: 'website',
          title: titles[family] ?? `${String(t.data.familyName)} — ${edition}`,
          detail: [
            String(t.data.familyName),
            family === 'session_feedback' ? 'counts as attendance' : null,
            family === 'event_registration' ? 'registered, not proof of attendance' : null,
          ]
            .filter(Boolean)
            .join(' · '),
        });
      }
      if (t.type === 'attendance' && t.data.source === 'gravity_survey') return;
      if (t.type === 'quiz') {
        out.push({
          at: t.at,
          source: 'moodle',
          title: 'Quiz in session recording',
          detail: [t.data.activityName as string | null, `${t.data.rawScore} / ${t.data.maxScore}`]
            .filter(Boolean)
            .join(' · '),
        });
        return;
      }
      if (t.type === 'attendance' && t.data.source === 'moodle') {
        out.push({
          at: t.at,
          source: 'moodle',
          title: 'Watched session recording',
          detail: [t.data.recording as string | null, t.data.courseName as string | null].filter(Boolean).join(' · '),
        });
        return;
      }
      if (t.type === 'attendance') {
        const src = String(t.data.source ?? '').toLowerCase();
        const minutes = t.data.durationMinutes as number | null;
        out.push({
          at: t.at,
          source: 'zoom',
          title: 'Attended live session',
          detail: [
            t.data.sessionId ? `Session ${t.data.sessionId}` : null,
            minutes ? `${minutes} min` : null,
            src ? src.toUpperCase() : null,
          ]
            .filter(Boolean)
            .join(' · '),
        });
      }
      if (t.type === 'mentorship') {
        const season = orDash(t.data.seasonName as string | undefined);
        const role = titleCase(String(t.data.role ?? ''));
        if (t.data.event === 'preference_submitted') {
          out.push({
            at: t.at,
            source: 'mentorship',
            title: `Mentorship preferences submitted — ${season}`,
            detail: [role ? `Signed up as ${role.toLowerCase()}` : null, t.data.matched ? 'Matched' : 'Not matched']
              .filter(Boolean)
              .join(' · '),
          });
        }
        if (t.data.event === 'session_attended') {
          const withName = (t.data.with as { name?: string } | null)?.name;
          out.push({
            at: t.at,
            source: 'mentorship',
            title: `Mentorship session attended${withName ? ` with ${withName}` : ''}`,
            detail: role ? `As ${role.toLowerCase()}` : '',
          });
        }
        if (t.data.event === 'sessions') {
          const attended = (t.data.sessionsAttended as number | null) ?? 0;
          const scheduled = (t.data.sessionsScheduled as number | null) ?? 0;
          const paired = (t.data.pairedCount as number | null) ?? 0;
          const firstAt = t.data.firstSessionAt as string | null;
          out.push({
            at: t.at,
            source: 'mentorship',
            title: `Mentorship sessions — ${season}`,
            detail: [
              `${attended} of ${scheduled} attended`,
              paired ? `${paired} ${paired === 1 ? 'pairing' : 'pairings'}` : null,
              role || null,
              firstAt ? `first session ${dateShort(firstAt)}` : null,
            ]
              .filter(Boolean)
              .join(' · '),
          });
        }
      }
    });

    data.moodleSummary.forEach((m) => {
      const at = m.dataDate ?? m.lastAccess;
      if (!at) return;
      const done = String(m.completionStatus ?? '')
        .toLowerCase()
        .includes('complet');
      out.push({
        at,
        source: 'moodle',
        title: done ? 'Course completed' : 'Enrolled in course',
        detail: m.courseName,
      });
    });

    return out
      .filter((e) => !cutoff || new Date(e.at) >= cutoff)
      .sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());
  }, [data, cutoff]);

  if (isLoading) {
    return (
      <Panel>
        <LoadingPanel label="Loading member…" height={360} />
      </Panel>
    );
  }

  if (error || !data) {
    return <ErrorNotice>Could not load this member. They may have been removed.</ErrorNotice>;
  }

  const { user, profile } = data;
  const since = profile?.submittedAt ?? user.createdAt;
  const recordingsWatched = data.moodleSummary.reduce(
    (n, m) => n + (m.activityPairs ?? []).filter((p) => p.completed).length,
    0,
  );
  const yearAgo = new Date();
  yearAgo.setFullYear(yearAgo.getFullYear() - 1);
  const eventsLastYear = data.timeline.filter((t) => t.type === 'attendance' && new Date(t.at) >= yearAgo).length;

  const mentorship = data.mentorshipSummary;
  const mentorshipSeasons = mentorship?.seasons ?? [];
  const inMentorship = mentorshipSeasons.length > 0;
  const mentorshipRoles = (mentorship?.roles ?? []).map(titleCase).join(' & ');
  const mentorshipMeta = inMentorship
    ? [
        mentorshipRoles || 'Participant',
        mentorshipSeasons.length === 1 ? mentorshipSeasons[0].seasonName : `${mentorshipSeasons.length} seasons`,
      ].join(' · ')
    : 'No mentorship participation';

  return (
    <>
      <div className="mb-6">
        <button onClick={() => navigate('/members')} className="font-semibold text-small text-ink-2 hover:text-ink">
          ← Members
        </button>
        <h1 className="font-serif font-semibold text-[42px] leading-none mt-3">
          {user.firstName} {user.lastName}
        </h1>
        <div className="flex items-end justify-between gap-6 mt-2">
          <p className="text-table text-ink-2">
            Member since {dateLong(since)} · {tenure(since)} with DIVERT
          </p>
          <Button size="sm" variant="outline" onClick={() => setLetterOpen(true)}>
            Attendance letter
          </Button>
        </div>
      </div>
      {letterOpen && userId ? (
        <AttendanceLetter
          userId={userId}
          name={`${user.firstName ?? ''} ${user.lastName ?? ''}`.trim()}
          onClose={() => setLetterOpen(false)}
        />
      ) : null}

      <div className="grid gap-6 items-start" style={{ gridTemplateColumns: '1.5fr 1fr' }}>
        <Panel>
          <PanelHeader title="Engagement timeline">
            <label className="flex items-center gap-2">
              <span className="font-semibold text-small text-ink-2">Window</span>
              <select
                value={win}
                onChange={(e) => setWin(e.target.value)}
                className="border border-line-strong bg-white px-2 py-[6px] text-small"
              >
                {WINDOWS.map((w) => (
                  <option key={w.key} value={w.key}>
                    {w.label}
                  </option>
                ))}
              </select>
            </label>
          </PanelHeader>

          {events.length === 0 ? (
            <EmptyState title="No activity in this window">
              This member has no recorded events from any connected source in the selected window. Widen the window to
              see their full history.
            </EmptyState>
          ) : (
            <div className="px-5">
              {events.map((e, i) => (
                <div
                  key={`${e.at}-${i}`}
                  className="grid gap-4 py-4 border-b border-line last:border-b-0"
                  style={{ gridTemplateColumns: '92px 1fr' }}
                >
                  <span className="text-small text-ink-2 tabular pt-[2px]">{dateShort(e.at)}</span>
                  <div className="flex gap-3">
                    <span className={`w-1 shrink-0 ${STRIPE[e.source]}`} />
                    <div className="min-w-0">
                      <div className="font-semibold text-small text-ink-2">{SOURCE_LABEL[e.source]}</div>
                      <div className="text-table font-semibold mt-[3px]">{e.title}</div>
                      <div className="text-small text-ink-2 mt-[2px]">{e.detail}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Panel>

        <div className="flex flex-col gap-6">
          <Panel>
            <PanelHeader title="Identity">
              <Pill tone={user.isActive ? 'active' : 'inactive'}>{user.isActive ? 'Active' : 'Inactive'}</Pill>
            </PanelHeader>
            <PanelBody className="pt-1">
              <KeyValue label="Email" value={user.email} />
              <KeyValue label="Organization" value={orDash(profile?.organization)} />
              <KeyValue label="Sector" value={orDash(profile?.sector)} />
              <KeyValue label="Career stage" value={orDash(profile?.careerSeniority)} />
              <KeyValue label="Discipline" value={orDash(profile?.discipline)} />
              <KeyValue label="Cluster" value={orDash(profile?.geographicRegion)} />
              <KeyValue label="Language" value={orDash(profile?.preferredLanguage)} />
              <KeyValue label="Pronouns" value={orDash(profile?.preferredPronouns)} />
            </PanelBody>
          </Panel>

          <Panel>
            <PanelHeader title="Engagement" />
            <MiniKpiGrid
              cells={[
                { label: 'Session recordings watched', value: recordingsWatched },
                { label: 'Events, last 12 months', value: eventsLastYear },
                {
                  label: 'Mentorship sessions attended',
                  value: inMentorship ? (mentorship?.sessionsAttended ?? 0) : DASH,
                },
                { label: 'Membership tenure', value: tenure(since) },
              ]}
            />
          </Panel>

          <Panel>
            <PanelHeader title="Linked identities" />
            <PanelBody className="pt-1">
              <LinkRow
                name="IBM Cloud Directory"
                meta={
                  user.ibmCloudId
                    ? user.ibmLastLoginAt
                      ? `Last sign-in ${dateShort(user.ibmLastLoginAt)}`
                      : 'Never signed in'
                    : 'No account provisioned'
                }
                linked={Boolean(user.ibmCloudId)}
                canProvision={mutable}
                provisioning={provision.isLoading}
                onProvision={() => provision.mutate()}
              />
              {provision.isError ? (
                <div className="mt-3">
                  <ErrorNotice>
                    Provisioning failed.{' '}
                    {(provision.error as { response?: { data?: { error?: string } } })?.response?.data?.error ??
                      'Check the IBM credentials and try again.'}
                  </ErrorNotice>
                </div>
              ) : null}
              {provision.data ? (
                <p className="text-small text-ink-2 mt-3">
                  {provision.data.status === 'adopted_existing'
                    ? 'An existing directory account was found and linked. No welcome email was sent, because the platform does not know that account’s password.'
                    : provision.data.emailSent
                      ? 'Account created and the welcome email was sent.'
                      : `Account created, but the welcome email did not send${
                          provision.data.emailError ? `: ${provision.data.emailError}` : ''
                        }. The member cannot sign in until they receive a password.`}
                </p>
              ) : null}
              <LinkRow
                name="Moodle LMS"
                meta={user.moodleUserId ? `Moodle user ${user.moodleUserId}` : 'Not enrolled'}
                linked={Boolean(user.moodleUserId)}
              />
              <LinkRow name="Mentorship app" meta={mentorshipMeta} linked={inMentorship} />
              <p className="text-small text-ink-2 mt-4 pt-3 border-t border-line">
                Equity characteristics are not shown per member — they exist only as aggregates in Analytics.
              </p>
            </PanelBody>
          </Panel>

          {mentorship && inMentorship ? <MemberMentorship detail={mentorship} /> : null}

          <MemberPrograms userId={user.id} />
          <MemberCommittees userId={user.id} />
          <MemberEvents userId={user.id} />
          {mutable ? <MemberPayments userId={user.id} /> : null}
          {mutable ? <MemberStipend userId={user.id} /> : null}

          <MemberAccount
            userId={user.id}
            email={user.email}
            firstName={user.firstName}
            lastName={user.lastName}
            isActive={user.isActive}
            ibmLinked={Boolean(user.ibmCloudId)}
            ibmDeletedAt={user.ibmDeletedAt}
            archivedAt={user.archivedAt}
            mutable={mutable}
          />
        </div>
      </div>
    </>
  );
}
